import { RefCallback, useLayoutEffect, useRef, useState } from 'react'

export type VirtualRowRange = {
    /** First row to show. */
    start: number
    /** One past the last row to show, so rows.slice(start, end) just works. */
    end: number
    /** How tall one row is in pixels, including its border. */
    pitch: number
}

type Props = {
    /** How many rows there are in total. */
    rowCount: number
    /** Expected height of one row in pixels. Used until real rows can be measured. */
    nominalRowHeight: number
    /**
     * How many extra rows to render above and below what is visible, so
     * scrolling stays smooth. Defaults to 3.
     */
    overscan?: number
    /** Set to false to show all rows and turn virtualization off. */
    enabled?: boolean
    /**
     * How to find the rendered rows. When set, row height is measured from
     * the real rows on screen, so borders around them are counted too.
     */
    rowSelector?: string
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value))
}

function measurePitch(content: HTMLElement, rowSelector: string | undefined, nominalRowHeight: number): number {
    if (rowSelector) {
        const direct = content.querySelectorAll(`:scope > ${rowSelector}`)
        const rendered = direct.length > 0 ? direct : content.querySelectorAll(`:scope ${rowSelector}`)
        const first = rendered[0]
        const second = rendered[1]
        if (first instanceof HTMLElement && second instanceof HTMLElement) {
            // The gap between two neighbours includes borders and spacing.
            const pitch = second.offsetTop - first.offsetTop
            if (pitch > 0) return pitch
        }
        if (rendered.length === 1 && first instanceof HTMLElement && first.offsetHeight > 0) {
            return first.offsetHeight
        }
    }
    return nominalRowHeight
}

function findClipAncestors(content: HTMLElement): HTMLElement[] {
    const ancestors: HTMLElement[] = []
    let parent = content.parentElement
    while (parent) {
        if (getComputedStyle(parent).overflowY !== 'visible') ancestors.push(parent)
        parent = parent.parentElement
    }
    return ancestors
}

// Rows to show before measuring: enough to fill the screen. Too many is harmless, but zero would briefly show an empty table.
function initialEnd(rowCount: number, nominalRowHeight: number, overscan: number): number {
    if (typeof window === 'undefined' || nominalRowHeight <= 0) return Math.min(overscan, rowCount)
    return Math.min(rowCount, Math.ceil(window.innerHeight / nominalRowHeight) + overscan)
}

/**
 * Figures out which rows are on screen, so only those need rendering. All
 * rows must have the same height. Callers show rows.slice(start, end) and
 * fill the skipped space above and below with blank spacers, so the
 * scrollbar keeps its full size.
 *
 * Handles both a list with its own scrollbar and a full-height list where
 * the page (or a parent box) does the scrolling.
 *
 * Attach the returned ref to the watched element. It is the element state
 * setter itself, so attaching, detaching, and node swaps all re-run the
 * measurement. Never wrap it: a new callback identity would detach and
 * re-attach the node on every render.
 */
export const useVirtualizeRows = ({
    rowCount,
    nominalRowHeight,
    overscan = 3,
    enabled = true,
    rowSelector,
}: Props): VirtualRowRange & { ref: RefCallback<HTMLElement> } => {
    const [range, setRange] = useState<VirtualRowRange>(() => ({
        start: 0,
        end: initialEnd(rowCount, nominalRowHeight, overscan),
        pitch: nominalRowHeight,
    }))
    const [element, setElement] = useState<HTMLElement | null>(null)
    const clipAncestors = useRef<HTMLElement[] | null>(null)
    const clipElement = useRef<HTMLElement | null>(null)
    const animationFrameId = useRef<number | null>(null)

    useLayoutEffect(() => {
        if (!enabled) {
            setRange(prev =>
                prev.start === 0 && prev.end === rowCount && prev.pitch === nominalRowHeight ? prev : { start: 0, end: rowCount, pitch: nominalRowHeight }
            )
            return
        }

        const applyRange = (start: number, end: number, pitch: number) => {
            setRange(prev => (prev.start === start && prev.end === end && prev.pitch === pitch ? prev : { start, end, pitch }))
        }

        function updateRange() {
            if (!element) return

            if (rowCount === 0) {
                applyRange(0, 0, nominalRowHeight)
                return
            }

            const rect = element.getBoundingClientRect()
            if (rect.height === 0 && element.clientHeight === 0) {
                // Hidden (e.g. inside a collapsed section): keep a few rows
                // ready so opening it paints instantly.
                applyRange(0, Math.min(overscan, rowCount), nominalRowHeight)
                return
            }

            const pitch = measurePitch(element, rowSelector, nominalRowHeight)

            // Only counts as its own scrollbar when more than one full row is
            // cut off; smaller differences are just rounding noise.
            if (element.scrollHeight - element.clientHeight > pitch) {
                const first = Math.floor(element.scrollTop / pitch)
                const last = Math.ceil((element.scrollTop + element.clientHeight) / pitch)
                applyRange(clamp(first - overscan, 0, rowCount), clamp(last + overscan, 0, rowCount), pitch)
                return
            }

            if (!clipAncestors.current || clipElement.current !== element) {
                clipElement.current = element
                clipAncestors.current = findClipAncestors(element)
            }
            let clipTop = 0
            let clipBottom = window.innerHeight
            for (const ancestor of clipAncestors.current) {
                const box = ancestor.getBoundingClientRect()
                clipTop = Math.max(clipTop, box.top)
                clipBottom = Math.min(clipBottom, box.bottom)
            }
            const visibleTop = Math.max(0, clipTop - rect.top)
            const visibleBottom = Math.min(rect.height, clipBottom - rect.top)

            if (visibleBottom <= 0) {
                applyRange(0, Math.min(overscan, rowCount), pitch)
                return
            }
            if (visibleTop >= rect.height) {
                applyRange(Math.max(0, rowCount - overscan), rowCount, pitch)
                return
            }
            const first = Math.floor(visibleTop / pitch)
            const last = Math.ceil(visibleBottom / pitch)
            applyRange(clamp(first - overscan, 0, rowCount), clamp(last + overscan, 0, rowCount), pitch)
        }

        if (!element) return

        const scheduleUpdate = () => {
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current)
            animationFrameId.current = requestAnimationFrame(updateRange)
        }

        updateRange()

        // One window listener catches scrolling from the list, any parent box,
        // and the page itself.
        window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true })
        window.addEventListener('resize', scheduleUpdate)
        const resizeObserver = new ResizeObserver(scheduleUpdate)
        resizeObserver.observe(element)
        // The list can move when content above it changes, even without scrolling.
        resizeObserver.observe(document.body)

        return () => {
            window.removeEventListener('scroll', scheduleUpdate, { capture: true })
            window.removeEventListener('resize', scheduleUpdate)
            resizeObserver.disconnect()
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current)
        }
    }, [element, rowCount, nominalRowHeight, overscan, enabled, rowSelector])

    return { ...range, ref: setElement }
}
